import * as React from "react";
import {
  TextField,
  Box,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Fade,
} from "@mui/material";
import { getHeaders } from "./utils/HeaderHelper";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

interface LoginPageContent {
  heading1: string;
  heading2: string;
  heading3: string;
  subheading1: string;
  subheading2: string;
  subheading3: string;
  image_url: string;
}

interface Props {
  csrfToken: string;
  logo_src: string;
  button_src: string;
  login_bg_img_src: string;
  login_page_content: any;
}

const PasswordExpiration: React.FC<Props> = (props: any) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [newPassword, setNewPassword] = React.useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] =
    React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    React.useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const [confirmPasswordError, setConfirmPasswordError] = React.useState("");

  const [rules, setRules] = React.useState({
    minLength: false,
    letter: false,
    number: false,
    specialChar: false,
  });

  const passwordRules = (pwd: string) => ({
    minLength: pwd.length >= 6,
    letter: /[a-zA-Z]/.test(pwd),
    number: /\d/.test(pwd),
    specialChar: /[^a-zA-Z0-9]/.test(pwd),
  });

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPwd = event.target.value;
    setNewPassword(newPwd);

    const r = passwordRules(newPwd);
    setRules(r);

    if (newPasswordConfirmation) {
      setConfirmPasswordError(
        newPwd === newPasswordConfirmation ? "" : "Passwords do not match"
      );
    }
  };

  const handleConfirmPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newConfirm = event.target.value;
    setNewPasswordConfirmation(newConfirm);

    setConfirmPasswordError(
      newConfirm === newPassword ? "" : "Passwords do not match"
    );
  };

  const isFormValid =
    rules.minLength &&
    rules.letter &&
    rules.number &&
    rules.specialChar &&
    newPassword === newPasswordConfirmation &&
    newPassword.length > 0;

  const disableSubmitButton = !isFormValid;

  React.useEffect(() => {
    fetch("/set_timezone", {
      method: "POST",
      headers: getHeaders(props.csrfToken),
      body: JSON.stringify({
        time_zone: timeZone,
      }),
    });
  }, [timeZone]);

  React.useEffect(() => {
    // Prevent navigating back to authenticated pages while forced password update is active.
    const lockBackNavigation = () => {
      window.history.pushState(null, "", window.location.href);
    };

    lockBackNavigation();
    window.addEventListener("popstate", lockBackNavigation);

    return () => {
      window.removeEventListener("popstate", lockBackNavigation);
    };
  }, []);

  const defaultSteps = [
    {
      label: "Smarter care starts here.",
      description: `Starfield helps you care for your patients with clarity and ease. Build custom questionnaires, manage immunizations, assign actions, and much more all in one simple, powerful platform.`,
    },
    {
      label: "Create an ad group",
      description:
        "Starfield helps you care for your patients with clarity and ease. Build custom questionnaires, manage immunizations, assign actions, and much more all in one simple, powerful platform.",
    },
    {
      label: "Create an ad group",
      description: `Starfield helps you care for your patients with clarity and ease. Build custom questionnaires, manage immunizations, assign actions, and much more all in one simple, powerful platform.`,
    },
  ];

  const steps = React.useMemo(() => {
    if (props.login_page_content) {
      return [
        {
          label: props.login_page_content.heading1 || defaultSteps[0].label,
          description:
            props.login_page_content.subheading1 || defaultSteps[0].description,
        },
        {
          label: props.login_page_content.heading2 || defaultSteps[1].label,
          description:
            props.login_page_content.subheading2 || defaultSteps[1].description,
        },
        {
          label: props.login_page_content.heading3 || defaultSteps[2].label,
          description:
            props.login_page_content.subheading3 || defaultSteps[2].description,
        },
      ];
    }
    return defaultSteps;
  }, [props.login_page_content]);

  const maxSteps = steps.length;

  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prevStep) => (prevStep + 1) % maxSteps);
    }, 5000);

    return () => clearInterval(interval);
  }, [maxSteps]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowPasswordConfirmation = () =>
    setShowPasswordConfirmation((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <Box
      sx={{
        backgroundImage: `linear-gradient(46.33deg, rgba(33, 33, 33, 0.839216) 0%, rgba(66, 66, 66, 0.239216) 178.98%), url(${
          props.login_page_content?.image_url || props.login_bg_img_src
        })`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: "100vh",
        display: "flex",
      }}
    >
      <Box
        sx={{
          width: "53%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "70%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            rowGap: "20px",
          }}
        >
          <img src={props.login_logo_src} alt="Project Starfield" />
          <Typography
            sx={{
              fontSize: "39.06px",
              fontWeight: 700,
              lineHeight: "46.87px",
              color: "white",
              fontFamily: "QuicksandMedium",
            }}
          >
            {steps[activeStep].label}
          </Typography>
          <Box
            sx={{
              minHeight: "85px",
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: "28.16px",
                color: "white",
                fontFamily: "QuicksandLight",
              }}
            >
              {steps[activeStep].description}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {steps.map((_, index) => (
              <Box
                key={index + 1}
                sx={{
                  height: activeStep === index ? "5px" : "3px",
                  transition: "all 0.3s ease",
                  width: activeStep === index ? "48px" : "32px",
                  backgroundColor: activeStep === index ? "white" : "#9E9E9E",
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          width: "47%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
        }}
      >
        <Box
          sx={{
            width: "53%",
            height: "75vh",
            backgroundColor: "#EAEAEA",
            borderTopLeftRadius: "24px",
            borderTopRightRadius: "24px",
            padding: "34px",
          }}
        >
          <Typography
            sx={{
              fontSize: "12.8px",
              fontWeight: 400,
              lineHeight: "22.53px",
              fontFamily: "QuicksandRegular",
            }}
          >
            YOU'RE PASSWORD HAS EXPIRED
          </Typography>
          <Typography
            sx={{
              fontSize: "25px",
              fontWeight: 500,
              lineHeight: "44px",
              marginBottom: "20px",
              fontFamily: "QuicksandRegular",
            }}
          >
            Update your password
          </Typography>
          <form
            id="loginform"
            action="/password_expiration"
            method="post"
            style={{ display: "flex", flexDirection: "column", rowGap: "20px" }}
            autoComplete="new-password"
          >
            <input
              type="hidden"
              name="authenticity_token"
              value={props.csrfToken}
            />

            <Box sx={{ mt: 1, mb: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem", mb: 1, display: "block" }}
              >
                Password requirements:
              </Typography>

              <PasswordRule
                valid={rules.minLength}
                label="At least 6 characters"
              />
              <PasswordRule
                valid={rules.letter}
                label="At least one letter (a-z, A-Z)"
              />
              <PasswordRule
                valid={rules.number}
                label="At least one number (0-9)"
              />
              <PasswordRule
                valid={rules.specialChar}
                label="At least one special character (! $ @ % #)"
              />

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.7rem",
                  display: "block",
                  mt: 1,
                  fontStyle: "italic",
                }}
              >
                • Cannot reuse a recent password
              </Typography>
            </Box>

            <TextField
              required
              fullWidth
              value={newPassword}
              name="user[new_password]"
              label="New Password*"
              autoComplete="new-password"
              inputProps={{
                autoComplete: "new-password",
                readOnly: true,
                onFocus: (e) => {
                  e.target.removeAttribute("readonly");
                  e.target.setAttribute("autocomplete", "new-password");
                },
              }}
              onChange={handlePasswordChange}
              type={showPassword ? "text" : "password"}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword
                            ? "hide the password"
                            : "display the password"
                        }
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        onMouseUp={handleMouseUpPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                },
                inputLabel: {
                  required: false,
                },
              }}
              sx={textfieldStyle}
            />

            <TextField
              required
              fullWidth
              value={newPasswordConfirmation}
              name="user[new_password_confirmation]"
              label="Verify New Password*"
              onChange={handleConfirmPasswordChange}
              type={showPasswordConfirmation ? "text" : "password"}
              autoComplete="new-password"
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword
                            ? "hide the password"
                            : "display the password"
                        }
                        onClick={handleClickShowPasswordConfirmation}
                        onMouseDown={handleMouseDownPassword}
                        onMouseUp={handleMouseUpPassword}
                        edge="end"
                      >
                        {showPasswordConfirmation ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                },
                inputLabel: {
                  required: false,
                },
              }}
              sx={textfieldStyle}
            />

            <Button
              type="submit"
              disabled={disableSubmitButton}
              fullWidth
              sx={loginButtonStyle}
            >
              Continue
            </Button>
          </form>
        </Box>
      </Box>
    </Box>
  );
};

const textfieldStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#BDBDBD",
      borderRadius: "8px",
    },
    "&:hover fieldset": {
      borderColor: "#f8890b",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#f8890b",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "14px !important",
    fontSize: "14px",
    WebkitBoxShadow: "0 0 0 1000px #EAEAEA inset",
    transition: "background-color 5000s ease-in-out 0s",
  },
  "& label": {
    color: "#BEBEBE",
  },
  "& label.Mui-focused": {
    color: "#424242",
  },
};

const loginButtonStyle = {
  backgroundColor: "#f8890b",
  color: "#F7F7F7",
  fontSize: "12.8px",
  lineHeight: "22.53px",
  fontWeight: 700,
  height: "50px",
  borderRadius: "8px",
  "&.Mui-disabled": {
    backgroundColor: "#cccccc",
    color: "gray",
  },
};

const PasswordRule = ({ label, valid }) => (
  <Fade in timeout={300}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
      {valid ? (
        <CheckCircleIcon sx={{ color: "green", fontSize: "16px", mr: 1 }} />
      ) : (
        <RadioButtonUncheckedIcon
          sx={{ color: "#aaa", fontSize: "16px", mr: 1 }}
        />
      )}

      <Typography
        variant="caption"
        sx={{
          fontSize: "0.7rem",
          color: valid ? "green" : "text.secondary",
          textDecoration: valid ? "line-through" : "none",
        }}
      >
        {label}
      </Typography>
    </Box>
  </Fade>
);

export default PasswordExpiration;
